<template>
  <div class="branch-details-page">
    <div
      v-if="loading"
      class="d-flex justify-content-center align-items-center py-5"
    >
      <MainLoader />
    </div>

    <div v-else-if="branchData" class="branch-content fade-in">
      <!-- Top Title Area -->
      <div class="mb-4 d-flex align-items-center justify-content-between">
        <h3 class="text-primary font-weight-bold">
          <v-icon color="primary" large class="ml-2">fas fa-store</v-icon>
          تفاصيل الفرع
        </h3>
      </div>

      <div class="row">
        <!-- 1. Basic Info Card -->
        <div class="col-lg-4 mb-4">
          <v-card class="creative-card h-100" elevation="2">
            <div
              class="card-cover"
              :style="
                coverImage ? { backgroundImage: `url(${coverImage})` } : {}
              "
            >
              <div class="cover-overlay"></div>
            </div>

            <div class="card-avatar-wrapper">
              <v-avatar size="100" class="card-avatar shadow-lg">
                <img
                  :src="branchData.icon"
                  alt="Branch Logo"
                  v-if="branchData.icon"
                />
                <v-icon v-else size="50" color="primary">fas fa-store</v-icon>
              </v-avatar>
            </div>

            <v-card-text class="text-center pt-5 mt-4">
              <h4 class="font-weight-bold text-dark mb-1">
                {{ branchData.name }}
              </h4>
              <p class="text-muted mb-3">
                {{ formatStoreType(branchData.store_type) }}
              </p>

              <div class="d-flex justify-content-center flex-wrap gap-2 mb-3">
                <v-chip
                  :color="branchData.is_active ? 'success' : 'error'"
                  outlined
                  small
                >
                  <v-icon right small class="ml-1">{{
                    branchData.is_active ? "fas fa-check" : "fas fa-times"
                  }}</v-icon>
                  {{ branchData.is_active ? "نشط" : "غير نشط" }}
                </v-chip>

                <v-chip color="red" outlined small>
                  <v-icon right small class="ml-1">fas fa-heart</v-icon>
                  {{ branchData.favorite_count }} مفضلة
                </v-chip>

                <v-chip
                  color="orange"
                  outlined
                  small
                  v-if="branchData.is_have_meals"
                >
                  <v-icon right small class="ml-1">fas fa-hamburger</v-icon>
                  يوجد وجبات
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- 2. Location & Map Card -->
        <div class="col-lg-8 mb-4">
          <v-card class="creative-card h-100" elevation="2">
            <v-card-title class="card-title-styled">
              <v-icon color="info" class="ml-2">fas fa-map-marker-alt</v-icon>
              الموقع والتفاصيل
            </v-card-title>
            <v-card-text>
              <div class="location-box mb-3 p-3 bg-light rounded">
                <v-icon small color="grey" class="ml-2">fas fa-map</v-icon>
                <span class="font-weight-medium">{{
                  branchData.location
                }}</span>
              </div>

              <div
                class="map-container rounded overflow-hidden shadow-sm border"
              >
                <GmapMap
                  v-if="branchData.lat && branchData.lng"
                  :center="{ lat: branchData.lat, lng: branchData.lng }"
                  :zoom="15"
                  style="width: 100%; height: 250px"
                  :options="{
                    zoomControl: true,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: true,
                    disableDefaultUi: false,
                  }"
                >
                  <GmapMarker
                    :position="{ lat: branchData.lat, lng: branchData.lng }"
                    :icon="branchData.icon"
                    :title="branchData.name"
                  />
                </GmapMap>
                <div v-else class="text-center py-5 bg-light">
                  <v-icon large color="grey">fas fa-map-slash</v-icon>
                  <p class="mt-2 text-muted">لا يوجد إحداثيات للموقع</p>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- 3. Menus Section -->
        <div class="col-lg-7 mb-4" v-if="false">
          <v-card class="creative-card h-100" elevation="2">
            <v-card-title class="card-title-styled">
              <v-icon color="warning" class="ml-2">fas fa-utensils</v-icon>
              الأقسام والقوائم
            </v-card-title>
            <v-card-text>
              <div
                v-if="branchData.menus && branchData.menus.length > 0"
                class="menus-grid mt-2"
              >
                <div
                  v-for="menu in sortedMenus"
                  :key="menu.id"
                  class="menu-item-card"
                >
                  <div class="menu-icon">
                    <v-icon color="primary" small>fas fa-concierge-bell</v-icon>
                  </div>
                  <div class="menu-info">
                    <span class="menu-name">{{ menu.name }}</span>
                  </div>
                  <div class="menu-priority" v-if="menu.priority">
                    #{{ menu.priority }}
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-5">
                <v-icon size="50" color="grey lighten-2"
                  >fas fa-folder-open</v-icon
                >
                <p class="mt-3 text-muted">لا توجد قوائم مضافة لهذا الفرع</p>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- 4. Working Hours Section -->
        <div class="col-lg-5 mb-4">
          <v-card class="creative-card h-100" elevation="2">
            <v-card-title class="card-title-styled">
              <v-icon color="success" class="ml-2">fas fa-clock</v-icon>
              أوقات العمل
            </v-card-title>
            <v-card-text class="p-0">
              <v-list class="working-hours-list">
                <v-list-item
                  v-for="time in branchData.working_times"
                  :key="time.id"
                  class="working-hour-row border-bottom py-2"
                  :class="{ 'opacity-70': !time.enabled }"
                >
                  <v-list-item-content>
                    <v-list-item-title class="d-flex align-items-center">
                      <div
                        class="status-indicator ml-3"
                        :class="time.enabled ? 'open' : 'closed'"
                      ></div>
                      <span class="font-weight-bold">{{
                        getDayName(time.day)
                      }}</span>
                    </v-list-item-title>
                  </v-list-item-content>

                  <v-list-item-action class="m-0 align-self-center">
                    <div
                      v-if="time.enabled && time.times && time.times.length > 0"
                      class="time-slots text-left"
                    >
                      <div
                        v-for="(t, index) in time.times"
                        :key="index"
                        class="time-slot-badge mb-1"
                      >
                        <span dir="ltr"
                          >{{ formatTime(t.start_time) }} -
                          {{ formatTime(t.end_time) }}</span
                        >
                      </div>
                    </div>
                    <div v-else class="closed-badge">مغلق</div>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ShowBranch",
  props: ["branch_id"],
  data() {
    return {
      branch: JSON.parse(localStorage.getItem("zoz_store_branch")),
      branchData: null,
      loading: true,
    };
  },
  computed: {
    coverImage() {
      if (this.branchData?.images?.length > 0) {
        return this.branchData.images[0].image;
      }
      return "";
    },
    sortedMenus() {
      if (!this.branchData || !this.branchData.menus) return [];
      return [...this.branchData.menus].sort((a, b) => {
        if (a.priority === null) return 1;
        if (b.priority === null) return -1;
        return a.priority - b.priority;
      });
    },
  },
  watch: {
    clear_fileds() {
      this.fetchBranchDetails();
    },
  },
  methods: {
    fetchBranchDetails() {
      this.loading = true;
      this.$axios({
        method: "GET",
        url: `show_place/${this.branch_id}`,
      })
        .then((res) => {
          this.branchData = res.data.data;
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          // Fallback to local storage branch if api fails
          if (this.branch_id) {
            this.branchData = this.branch;
          } else {
            this.$iziToast.error({
              timeout: 3000,
              message: "حدث خطأ أثناء جلب التفاصيل",
              position: "bottomRight",
            });
          }
        });
    },
    getDayName(dayEn) {
      const days = {
        Saturday: "السبت",
        Sunday: "الأحد",
        Monday: "الاثنين",
        Tuesday: "الثلاثاء",
        Wednesday: "الأربعاء",
        Thursday: "الخميس",
        Friday: "الجمعة",
      };
      return days[dayEn] || dayEn;
    },
    formatTime(timeStr) {
      if (!timeStr) return "";
      const [hours, minutes] = timeStr.split(":");
      let h = parseInt(hours);
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12;
      h = h ? h : 12;
      return `${h}:${minutes} ${ampm}`;
    },
    formatStoreType(type) {
      if (!type) return "";
      return type
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
  },
  created() {
    this.fetchBranchDetails();
  },
};
</script>

<style lang="scss" scoped>
// General styling
.branch-details-page {
  padding: 1.5rem;
  direction: rtl;
}

.fade-in {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.gap-2 {
  gap: 0.5rem;
}

// Cards
.creative-card {
  border-radius: 20px !important;
  overflow: hidden;
  border: 1px solid #eef2f7;
  transition: all 0.3s ease;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.03) !important;
  background: #fff;

  &:hover {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08) !important;
    transform: translateY(-3px);
  }

  .card-title-styled {
    font-size: 1.2rem;
    font-weight: 700;
    color: #334155;
    padding: 1.25rem 1.25rem 0.75rem;
    border-bottom: 1px solid #f1f5f9;
    margin-bottom: 0.5rem;
  }
}

// Basic Info Card Specifics
.card-cover {
  height: 120px;
  background-size: cover;
  background-position: center;
  position: relative;
  background-color: #4e73df; // fallback

  .cover-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.1),
      rgba(0, 0, 0, 0.5)
    );
  }
}

.card-avatar-wrapper {
  display: flex;
  justify-content: center;
  margin-top: -50px;
  position: relative;
  z-index: 2;

  .card-avatar {
    border: 4px solid #fff;
    background-color: #fff;
    img {
      object-fit: contain;
    }
  }
}

// Map Card Specifics
.map-container {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

// Menus Card Specifics
.menus-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;

  .menu-item-card {
    display: flex;
    align-items: center;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 10px 15px;
    transition: all 0.2s;

    .menu-icon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: #eff6ff;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 12px;
    }

    .menu-info {
      flex: 1;
      .menu-name {
        font-weight: 600;
        color: #1e293b;
        font-size: 0.95rem;
      }
    }

    .menu-priority {
      background: #e2e8f0;
      color: #475569;
      font-size: 0.75rem;
      padding: 2px 8px;
      border-radius: 10px;
      font-weight: 700;
    }

    &:hover {
      background: #fff;
      border-color: #cbd5e1;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
        0 2px 4px -1px rgba(0, 0, 0, 0.06);
      transform: translateY(-2px);
    }
  }
}

// Working Hours Specifics
.working-hours-list {
  background: transparent !important;

  .working-hour-row {
    padding: 12px 20px;

    &:last-child {
      border-bottom: none !important;
    }

    .status-indicator {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      &.open {
        background-color: #10b981;
        box-shadow: 0 0 6px rgba(#10b981, 0.5);
      }
      &.closed {
        background-color: #cbd5e1;
      }
    }

    .time-slot-badge {
      background-color: #f1f5f9;
      color: #334155;
      font-weight: 600;
      font-size: 0.85rem;
      padding: 4px 10px;
      border-radius: 6px;
      display: inline-block;
      border: 1px solid #e2e8f0;
    }

    .closed-badge {
      background-color: #f8fafc;
      color: #94a3b8;
      font-weight: 500;
      font-size: 0.85rem;
      padding: 4px 10px;
      border-radius: 6px;
      border: 1px solid #e2e8f0;
    }
  }

  .opacity-70 {
    opacity: 0.7;
  }
}
</style>
